import { WhitelistSale, WhitelistSaleFactory } from '@arcxgame/contracts/dist/src/typings'
import { useAsync } from '@arcxmoney/arcx-react'
import { BigNumber } from 'ethers'
import { useContext } from 'react'
import { getAddressesForNetwork } from 'src/config/getAddressesForNetwork'
import { ArcContext } from 'src/contexts/ArcContext'

interface Participant {
  allocation: BigNumber
  spent: BigNumber
}

interface EligibilityInfo {
  participant: Participant
  currency: string
  whitelistContract: WhitelistSale
  hardcap: BigNumber
  totalRaised: BigNumber
  saleStarted: boolean
}

const invalidParticipant = {
  allocation: BigNumber.from(0),
  spent: BigNumber.from(0),
}

const invalidEligibility = {
  participant: invalidParticipant,
  currency: undefined,
  whitelistContract: undefined,
  hardcap: undefined,
  totalRaised: undefined,
  saleStarted: undefined,
}

export const useSaleEligibility = (phase: number): EligibilityInfo => {
  const { arc, network, isPlaynetActivated } = useContext(ArcContext)
  const [eligibilityInfo] = useAsync<EligibilityInfo>(async () => {
    if (!arc || !arc.signer) {
      return invalidEligibility
    }

    const contractName = phase === 1 ? 'WhitelistSale' : 'WhitelistSale-2'
    const allAddresses = getAddressesForNetwork(network, isPlaynetActivated)
    const whitelistContractInfo = allAddresses.find(
      (contractInfo) => contractInfo.name === contractName,
    )

    if (!whitelistContractInfo) {
      console.error('WhitelistSale contract not found in the deployments!')
      return invalidEligibility
    }

    // const whitelistSaleContract = WhitelistSaleFactory.connect('0x7C23551E77E42386D557C139Eb69E307c88eAf2F', arc.signer) // rinkeby
    // const whitelistSaleContract = WhitelistSaleFactory.connect('0x9B66C024A43C1bdbb90eD3957A3b5Ba4e9398606', arc.signer) // playnet
    // const whitelistSaleContract = WhitelistSaleFactory.connect('0x06270c6aF7F20559a6dC611A4d76b6FA398Cf454', arc.signer) // mainnet
    const whitelistSaleContract = WhitelistSaleFactory.connect(
      whitelistContractInfo.address,
      arc.signer,
    )

    const participant = await whitelistSaleContract.participants(await arc.signer.getAddress())
    const currency = await whitelistSaleContract.currency()
    const hardcap = await whitelistSaleContract.totalHardCap()
    const totalRaised = await whitelistSaleContract.totalRaised()
    const saleStarted = await whitelistSaleContract.saleOpen()

    return {
      participant: participant || invalidParticipant,
      currency,
      whitelistContract: whitelistSaleContract,
      hardcap,
      totalRaised,
      saleStarted,
    }
  }, [arc, network])

  return eligibilityInfo || invalidEligibility
}
